// Add at the top with other imports
import AgentInformationModal from '../agent/AgentInformationModal';
import React, { useState, forwardRef, useEffect, useImperativeHandle } from 'react';
import SystemAlert from '../../common/SystemAlert.js';
import AgentInstructionsModal from '../agent/AgentInstructionsModal';
import TextBubbleModal from './TextBubbleModal';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Drawer,
  Slider,
  Link,
} from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { v4 as uuidv4 } from 'uuid';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { HexColorPicker } from 'react-colorful';
import Popover from '@mui/material/Popover';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useReseller } from '../../contexts/ResellerContext';
import { API_URL } from '../../common/Constants';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import TabletIcon from '@mui/icons-material/Tablet';
import LaptopIcon from '@mui/icons-material/Laptop';
import GreetingFallbackModal from './GreetingFallbackModal';
import AdvancedEmailSettingsModal from './AdvancedEmailSettingsModal';

const MENU_OPTIONS = [
  { key: 'agentInformation', label: 'Agent Information' },
  { key: 'agentInstructions', label: 'Agent Instructions' },
  { key: 'agentTemperature', label: 'Agent Temperature' },
  { key: 'basic', label: 'Basic Appearance' },
  { key: 'logoSettings', label: 'Logo Settings' },
  { key: 'chat', label: 'Chat Settings' },
  { key: 'welcomeStep', label: 'Welcome Step' },
  { key: 'agentActions', label: 'Text Bubble' },
  { key: 'messageColors', label: 'Message & UI Colors' },
  { key: 'presets', label: 'Starter Prompts' },
  { key: 'quickReplies', label: 'Quick Replies' },
  { key: 'greeting', label: 'Greeting & Fallback' },
  { key: 'chatRating', label: 'Chat Rating' },
  { key: 'emailSettings', label: 'Email Settings' },
  { key: 'additionalSettings', label: 'Additional Settings' },
];



const MENU_WIDTH = 220;
const DRAWER_WIDTH = 400;

const BotCustomizerPanel = forwardRef((props, ref) => {
  const {
    customization,
    setCustomization,
    devicePreview,
    setDevicePreview,
    setExportDialogOpen,
    onTestIdle,
    onTestClose,
    onResetPreview,
    openDialog,
    setOpenDialog,
    agentId,
    selectedInstructionId,
    setSelectedInstructionId,
    agentInfoModalOpen,
    setAgentInfoModalOpen,
    newAgent,
    mode,
    setAlert,
    onCustomizationSaved,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    lastSavedCustomization,
    setLastSavedCustomization,
    resetCustomization,
    tenantId,
    ...rest
  } = props;

  // Expose saveAgentCustomization via ref for ChatMain
  // (already declared above)
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);
  const [textBubbleModalOpen, setTextBubbleModalOpen] = useState(false);
  const [greetingFallbackModalOpen, setGreetingFallbackModalOpen] = useState(false);
  const [advancedEmailModalOpen, setAdvancedEmailModalOpen] = useState(false);

  const openAdvancedEmailSettings = () => {
    setOpenDialog(null);
    setAdvancedEmailModalOpen(true);
  };

  const closeAdvancedEmailSettings = () => {
    setAdvancedEmailModalOpen(false);
    setOpenDialog('emailSettings');
  };

  const handleAdvancedEmailSaved = (updatedCustomization) => {
    setLastSavedCustomization && setLastSavedCustomization(updatedCustomization);
    setHasUnsavedChanges && setHasUnsavedChanges(false);
    localStorage.setItem('chatCustomization', JSON.stringify(updatedCustomization));
  };

  const handleResetAll = () => {
    if (resetCustomization) {
      resetCustomization();
    }
  };

  // agentInfoModalOpen and setAgentInfoModalOpen are now received as props from ChatMain
  // Add agentInfoModalOpen and setAgentInfoModalOpen to props

  // Popover color picker state for message colors
  const MESSAGE_COLOR_OPTIONS = [
    { key: 'botMessageColor', label: 'Bot Message Color' },
    { key: 'botTextColor', label: 'Bot Text Color' },
    { key: 'userMessageColor', label: 'User Message Color' },
    { key: 'userTextColor', label: 'User Text Color' },
    { key: 'chatBackgroundColor', label: 'Chat Background' },
    { key: 'headerBackgroundColor', label: 'Header/Closed Background' },
    { key: 'footerBackgroundColor', label: 'Footer Background' },
    { key: 'inputBoxColor', label: 'Input Box Color' },
    { key: 'sendIconColor', label: 'Input Color Busy' },
    { key: 'sendIconDisabledColor', label: 'Input Color Active' },
  ];

  const [editingColorKey, setEditingColorKey] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Default color values for message & UI colors
  const DEFAULT_MESSAGE_COLORS = {
    botMessageColor: '#E3F2FD',
    botTextColor: '#000000',
    userMessageColor: '#FFF9C4',
    userTextColor: '#000000',
    chatBackgroundColor: '#FFFFFF',
    headerBackgroundColor: '#1976d2',
    footerBackgroundColor: '#F5F5F5',
    inputBoxColor: '#FFFFFF',
    sendIconColor: '#1976d2',
    sendIconDisabledColor: '#BDBDBD',
    chatControlIconColor: '#555555',
  };

  const BASIC_APPEARANCE_COLOR_OPTIONS = [
    { key: 'agentNameColor', label: 'Agent Display Name Color', default: '#000000' },
    { key: 'botTitleColor', label: 'Agent Title Color', default: '#000000' },
  ];

  const TIMESTAMP_COLOR_OPTION = {
    key: 'timestampColor',
    label: 'Timestamp Color',
    default: '#888888',
  };

  const CHAT_CONTROLS_COLOR_OPTION = {
    key: 'chatControlIconColor',
    label: 'Chat Icon Controls',
    default: '#555555',
  };

  const handleResetMessageColors = () => {
    setCustomization(prev => ({ ...prev, ...DEFAULT_MESSAGE_COLORS }));
    setHasUnsavedChanges && setHasUnsavedChanges(true);
  };

  const handleSwatchClick = (key) => (event) => {
    setEditingColorKey(key);
    setAnchorEl(event.currentTarget);
  };

  const handleColorChange = (color) => {
    setCustomization(prev => ({ ...prev, [editingColorKey]: color }));
  };

  const handlePopoverClose = () => {
    setEditingColorKey(null);
    setAnchorEl(null);
  };

  // --- Response Settings State ---
  const DEFAULT_RESPONSE_LINK_TEXT = 'Read More';
  const [responseLinkTextError, setResponseLinkTextError] = useState('');
  const responseLinkEnabled = customization.responseLinkEnabled !== false;
  const responseLinkText = customization.responseLinkText !== undefined ? customization.responseLinkText : DEFAULT_RESPONSE_LINK_TEXT;

  const handleResponseLinkSwitch = (e) => {
    const checked = e.target.checked;
    setCustomization(prev => {
      let text = prev.responseLinkText;
      if (checked && (!text || text.trim() === '')) {
        text = DEFAULT_RESPONSE_LINK_TEXT;
      }
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return { ...prev, responseLinkEnabled: checked, responseLinkText: text };
    });
  };

  const handleResponseLinkTextChange = (e) => {
    let value = e.target.value;
    if (value.length > 30) value = value.slice(0, 30);
    setCustomization(prev => {
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return { ...prev, responseLinkText: value };
    });
  };

  useEffect(() => {
    if (responseLinkEnabled) {
      if (!responseLinkText || !responseLinkText.trim()) {
        setResponseLinkTextError('Link text is required.');
      } else {
        setResponseLinkTextError('');
      }
    } else {
      setResponseLinkTextError('');
    }
  }, [responseLinkEnabled, responseLinkText]);

  // Agent Temperature Drawer State
  const [agentTemperatureDrawerOpen, setAgentTemperatureDrawerOpen] = useState(false);
  // Handler for temperature change
  const handleTemperatureChange = (event, newValue) => {
    setCustomization(prev => ({ ...prev, temperature: newValue }));
  };
  // Drawer for Agent Temperature
  const renderAgentTemperatureDrawer = () => (
    <Drawer
      anchor="right"
      open={openDialog === 'agentTemperature'}
      onClose={() => setOpenDialog(null)}
      PaperProps={{ sx: { width: DRAWER_WIDTH } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Agent Temperature</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Controls the randomness and creativity of the agent's responses.<br />
          <b>Low (0.2):</b> Consistent, precise. <b>High (0.8):</b> Creative, varied.<br />
          <b>0:</b> Deterministic. <b>1:</b> Maximum creativity.
        </Typography>
        <Box sx={{ mt: 4, mb: 2 }}>
          <Slider
            value={customization.temperature ?? 0.7}
            min={0}
            max={1}
            step={0.01}
            onChange={handleTemperatureChange}
            valueLabelDisplay="auto"
            marks={[{ value: 0, label: '0' }, { value: 0.2, label: 'Low' }, { value: 0.7, label: 'Default' }, { value: 0.8, label: 'High' }, { value: 1, label: '1' }]}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Recommended: 0.2 for accuracy, 0.8 for creativity. Default is 0.7.
        </Typography>
      </Box>
    </Drawer>
  );
  // ...existing code...
  // (renderAgentTemperatureDrawer is called in the render block)
  // Update function signature to include these props
  // (This is a comment for clarity, actual code is above)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [editingPresetText, setEditingPresetText] = useState('');

  const ensureLinksHaveTargetBlank = (html) => {
    if (!html) return html;
    return html.replace(/<a\s+([^>]*)>/gi, (match, attributes) => {
      const hasTarget = /target\s*=\s*["']_blank["']/i.test(attributes);
      if (hasTarget) return match;
      const newAttributes = attributes.trim() + ' target="_blank"';
      return `<a ${newAttributes}>`;
    });
  };

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    setCustomization((prev) => {
      const updated = { ...prev, [key]: value };
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return updated;
    });
  };

  const handleSwitch = (key) => (e) => {
    const checked = e.target.checked;
    setCustomization((prev) => {
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return { ...prev, [key]: checked };
    });
  };

  const updateIntermediate = (patch) => {
    setCustomization((prev) => {
      const current = prev.intermediate && typeof prev.intermediate === 'object' ? prev.intermediate : {};
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return {
        ...prev,
        intermediate: {
          enabled: false,
          width: prev.chatWidth || 400,
          height: prev.chatHeight || 600,
          mediaUrl: '',
          html: '',
          buttonTitle: 'Start Conversation',
          ...current,
          ...patch,
        },
      };
    });
  };

  const handleEditPreset = (preset) => {
    setEditingPreset(preset);
    setEditingPresetText(preset.text);
    setEditDialogOpen(true);
  };

  const handleSavePreset = () => {
    if (editingPreset) {
      const newResponses = customization.presetResponses.map(p =>
        p.id === editingPreset.id ? { ...p, text: editingPresetText } : p
      );
      setCustomization(prev => {
        setHasUnsavedChanges && setHasUnsavedChanges(true);
        return { ...prev, presetResponses: newResponses };
      });
    }
    setEditDialogOpen(false);
  };

  const handleAddPreset = () => {
    const newPreset = {
      id: uuidv4(),
      text: "",
      active: true
    };
    const newResponses = [...(customization.presetResponses || []), newPreset];
    setCustomization(prev => {
      setHasUnsavedChanges && setHasUnsavedChanges(true);
      return { ...prev, presetResponses: newResponses };
    });
    setEditingPreset(newPreset);
    setEditingPresetText("");
    setEditDialogOpen(true);
  };


  const { reseller } = useReseller();

  const saveAgentCustomization = async (customization) => {
    try {
      // Normalize automatedClosedAgentPhrases to an array before saving (no other fields changed)
      const payload = { ...customization };
      if (payload.greetingText != null && String(payload.greetingText).trim() !== '') {
        payload.greetingText = ensureLinksHaveTargetBlank(payload.greetingText);
      }
      if (payload.fallbackText != null && String(payload.fallbackText).trim() !== '') {
        payload.fallbackText = ensureLinksHaveTargetBlank(payload.fallbackText);
      }
      if (typeof payload.automatedClosedAgentPhrases === 'string') {
        payload.automatedClosedAgentPhrases = payload.automatedClosedAgentPhrases
          .split(/\r?\n/)
          .map(s => s.trim())
          .filter(Boolean);
      }

      // Save powered by logo URL in config when reseller has poweredByLogoPath
      if (reseller != null && reseller.poweredByLogoPath?.trim() && tenantId) {
        payload.poweredByLogoUrl = `${API_URL}/public/tenant/${tenantId}/powered-by-logo`;
        const rawHref = (reseller.poweredByUrl != null ? String(reseller.poweredByUrl).trim() : '')
          || (reseller.baseUrl != null ? String(reseller.baseUrl).trim() : '');
        if (rawHref) {
          payload.poweredByLogoHref = /^https?:\/\//i.test(rawHref)
            ? rawHref
            : `https://${rawHref.replace(/^\/+/, '')}`;
        } else {
          delete payload.poweredByLogoHref;
        }
      }

      await axios.post(
        '/agents/saveAgentCustomization',
        payload,
        { params: { agentId } }
      );
      localStorage.setItem('chatCustomization', JSON.stringify(payload));
      setAlert({ severity: 'success', message: 'Customization saved successfully!' });
      setTimeout(() => setAlert(null), 2500);
      setHasUnsavedChanges && setHasUnsavedChanges(false);
      setLastSavedCustomization && setLastSavedCustomization(payload);
      setCustomization && setCustomization(payload); // Ensure UI updates immediately
      // Call the callback if provided (for AgentCreation flow)
      if (onCustomizationSaved) {
        onCustomizationSaved(payload);
      }
      return true;
    } catch (error) {
      console.error('Error saving agent customization:', error);
      const d = error?.response?.data;
      const apiMsg = typeof d === 'string' ? d : d?.message;
      setAlert({ severity: 'error', message: apiMsg || 'Failed to save customization' });
      setTimeout(() => setAlert(null), 3000);
      return false;
    }
  };

  // --- Dialog content renderers ---
  const renderDialogContent = (key) => {
    switch (key) {
      case 'additionalSettings':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Response Settings
            </Typography>
            <Typography variant="body2" align='left' sx={{ mb: 1 }}>
              Control whether a "Read More" link is shown at the end of AI responses that use external sources. You can also customize the link text and formatting.
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Show Read More Link</Typography>
                <Switch
                  checked={responseLinkEnabled}
                  onChange={handleResponseLinkSwitch}
                  color="primary"
                />
              </Box>
            </FormControl>
            {responseLinkEnabled && (
              <>
                <TextField
                  fullWidth
                  label="Read More Link Text"
                  value={responseLinkText}
                  onChange={handleResponseLinkTextChange}
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 30 }}
                  required
                  error={!!responseLinkTextError}
                  helperText={responseLinkTextError}
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Sound Settings
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Sound</Typography>
                <Switch checked={customization.soundEnabled} onChange={handleSwitch('soundEnabled')} color="primary" />
              </Box>
            </FormControl>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Timestamp Settings
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Show Timestamps</Typography>
                <Switch checked={customization.showTimestamps} onChange={handleSwitch('showTimestamps')} />
              </Box>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Timestamp Format</InputLabel>
              <Select
                value={customization.timestampFormat || 'MM/DD/YYYY hh:mm a'}
                onChange={handleChange('timestampFormat')}
                label="Timestamp Format"
                disabled={!customization.showTimestamps}
              >
                <MenuItem value="MM/DD/YYYY hh:mm a">US Date & Time (04/15/2025 1:30 PM)</MenuItem>
                <MenuItem value="hh:mm a">12-hour time (1:30 PM)</MenuItem>
                <MenuItem value="HH:mm">24-hour time (13:30)</MenuItem>
                <MenuItem value="DD/MM/YYYY HH:mm">EU Date & Time (15/04/2025 13:30)</MenuItem>
                <MenuItem value="YYYY-MM-DD HH:mm">ISO Date & Time (2025-04-15 13:30)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography sx={{ width: 160, textAlign: 'left', pr: 1, fontSize: 15 }}>
                {TIMESTAMP_COLOR_OPTION.label}
              </Typography>
              <Box
                onClick={customization.showTimestamps ? handleSwatchClick(TIMESTAMP_COLOR_OPTION.key) : undefined}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background:
                    customization[TIMESTAMP_COLOR_OPTION.key] !== undefined && customization[TIMESTAMP_COLOR_OPTION.key] !== ''
                      ? customization[TIMESTAMP_COLOR_OPTION.key]
                      : TIMESTAMP_COLOR_OPTION.default,
                  border: '1px solid #ccc',
                  cursor: customization.showTimestamps ? 'pointer' : 'not-allowed',
                  mx: 1,
                  flexShrink: 0,
                  opacity: customization.showTimestamps ? 1 : 0.6,
                }}
                aria-label={`Pick ${TIMESTAMP_COLOR_OPTION.label}`}
              />
              <TextField
                label="Hex"
                value={
                  customization[TIMESTAMP_COLOR_OPTION.key] !== undefined && customization[TIMESTAMP_COLOR_OPTION.key] !== ''
                    ? customization[TIMESTAMP_COLOR_OPTION.key]
                    : TIMESTAMP_COLOR_OPTION.default
                }
                onChange={e => setCustomization(prev => ({ ...prev, [TIMESTAMP_COLOR_OPTION.key]: e.target.value }))}
                sx={{ width: 100, flexShrink: 0 }}
                inputProps={{ maxLength: 7 }}
                disabled={!customization.showTimestamps}
              />
            </Box>
            <Popover
              open={editingColorKey === TIMESTAMP_COLOR_OPTION.key}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2 }}>
                <HexColorPicker
                  color={customization[TIMESTAMP_COLOR_OPTION.key] || TIMESTAMP_COLOR_OPTION.default}
                  onChange={handleColorChange}
                />
              </Box>
            </Popover>

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" gutterBottom>Disclaimer</Typography>
              <Tooltip title="You can enter a disclaimer that will display at the bottom of your chat window or leave it blank to not include a disclaimer." placement="right">
                <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                  <span style={{ fontSize: 16, color: '#888' }}>ⓘ</span>
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              label="Disclaimer Text"
              multiline
              minRows={2}
              maxRows={2}
              value={customization.disclaimerText || ''}
              onChange={e => setCustomization(prev => ({ ...prev, disclaimerText: e.target.value }))}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="caption" sx={{ mb: 0.5 }}>Alignment</Typography>
              <ToggleButtonGroup
                value={customization.disclaimerAlign || 'left'}
                exclusive
                onChange={(_, val) => val && setCustomization(prev => ({ ...prev, disclaimerAlign: val }))}
                size="small"
                sx={{ mb: 1 }}
              >
                <ToggleButton value="left" aria-label="Left Align">
                  <FormatAlignLeftIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="center" aria-label="Center Align">
                  <FormatAlignCenterIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="right" aria-label="Right Align">
                  <FormatAlignRightIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        );
      case 'agentTemperature':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" align='left' sx={{ mb: 1 }}>
              Controls the randomness and creativity of the agent's responses.<br /><br />Low = accuracy<br />High = creativity<br />Default = balanced<br /><br />For example, If you ask “What’s a good dinner idea?”<br /><br />
              <b>Low (0.2):</b> “Pasta with tomato sauce.” (safe, common answer)<br /><br /> <b>Medium (0.7):</b> You could try grilled salmon with roasted veggies or a pasta dish with a twist.” (balanced variety)<br /><br />
              <b>High (1):</b> How about a fusion taco night with chocolate mole sauce and pineapple salsa?” (creative, unusual)
            </Typography>
            <Box sx={{ mt: 4, mb: 2 }}>
              <Slider
                value={customization.temperature ?? 0.7}
                min={0}
                max={1}
                step={null}
                onChange={handleTemperatureChange}
                valueLabelDisplay="auto"
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.2, label: 'Low' },
                  {
                    value: 0.7,
                    label: (
                      <span style={{
                        position: 'relative',
                        left: '-28px',
                        fontWeight: 600,
                        color: 'var(--primary-color, #1976d2)',
                        background: 'color-mix(in srgb, var(--primary-color, #1976d2) 12%, white)',
                        borderRadius: '6px',
                        padding: '2px 8px'
                      }}>
                        Default
                      </span>
                    )
                  },
                  { value: 0.8, label: 'High' },
                  { value: 1, label: '1' }
                ]}
                sx={{ width: 300 }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, mb: 2 }}>
              <Button
                variant={customization.temperature === 0 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setCustomization(prev => ({ ...prev, temperature: 0 }))}
              >0</Button>
              <Button
                variant={customization.temperature === 0.2 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setCustomization(prev => ({ ...prev, temperature: 0.2 }))}
              >Low (0.2)</Button>
              <Button
                variant={customization.temperature === 0.7 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setCustomization(prev => ({ ...prev, temperature: 0.7 }))}
              >Default (0.7)</Button>
              <Button
                variant={customization.temperature === 0.8 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setCustomization(prev => ({ ...prev, temperature: 0.8 }))}
              >High (0.8)</Button>
              <Button
                variant={customization.temperature === 1 ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setCustomization(prev => ({ ...prev, temperature: 1 }))}
              >1</Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Only the above options are available. Recommended: 0.2 for accuracy, 0.8 for creativity. Default is 0.7.
            </Typography>
          </Box>
        );
      case 'chatRating':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enable chat rating to prompt users for feedback after a chat session ends.
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Enable Chat Rating</Typography>
                <Switch
                  checked={customization.chatRatingEnabled !== false}
                  onChange={handleSwitch('chatRatingEnabled')}
                  color="primary"
                />
              </Box>
            </FormControl>
            {customization.chatRatingEnabled !== false && (
              <>
                <TextField
                  fullWidth
                  label="Rating Prompt"
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={customization.chatRatingPrompt || 'How would you rate your chat experience?'}
                  onChange={handleChange('chatRatingPrompt')}
                  sx={{ mb: 2 }}
                  placeholder="How would you rate your chat experience?"
                />
                <TextField
                  fullWidth
                  label="Thank You Message"
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={customization.chatRatingThankYou || 'Thank you for your feedback!'}
                  onChange={handleChange('chatRatingThankYou')}
                  sx={{ mb: 2 }}
                  placeholder="Thank you for your feedback!"
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography>Allow User Comments</Typography>
                    <Switch
                      checked={customization.chatRatingAllowComments === true}
                      onChange={handleSwitch('chatRatingAllowComments')}
                      color="primary"
                    />
                  </Box>
                </FormControl>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              The rating dialog will appear after the chat closes. Comments are limited to 200 characters.
            </Typography>
          </Box>
        );

      case 'emailSettings':
        return (
          <Box sx={{ p: 2 }}>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ pr: 2 }}>
                  <Typography>
                    Allow Visitor Transcript Requests
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Visitors can request a copy of their conversation when a chat session ends.
                  </Typography>
                </Box>

                <Switch
                  checked={customization.visitorTranscriptEnabled === true}
                  onChange={handleSwitch('visitorTranscriptEnabled')}
                  color="primary"
                  sx={{ mt: '-6px', flexShrink: 0 }}
                />
              </Box>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Chat Session Notifications
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Stay informed about new conversations without logging in.
            </Typography>

            <TextField
              fullWidth
              label="Notification Recipients"
              multiline
              minRows={2}
              maxRows={4}
              value={customization.chatHistoryEmails || ''}
              onChange={handleChange('chatHistoryEmails')}
              sx={{ mb: 1 }}
              placeholder="support@example.com, manager@example.com"
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Enter one or more email addresses separated by commas. These recipients always receive completed chat transcripts. When Advanced routing is enabled, the matching department emails are included as well.
            </Typography>

            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={openAdvancedEmailSettings}
              sx={{ cursor: 'pointer', fontWeight: 500 }}
            >
              Advanced department routing →
            </Link>

          </Box>
        );

      case 'basic':
        return (
          <Box sx={{ p: 2 }}>
            <Tooltip title="Provide a name for your Agent to be displayed above the reply chat bubbles." placement="top">
              <TextField
                fullWidth
                label="Agent Display Name"
                value={customization.agentName || ''}
                onChange={handleChange('agentName')}
                sx={{ mb: 2 }}
                placeholder="Enter agent name (optional)"
              />
            </Tooltip>
            <TextField fullWidth label="Agent Title" value={customization.botTitle} onChange={handleChange('botTitle')} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
              {BASIC_APPEARANCE_COLOR_OPTIONS.map(opt => (
                <Box key={opt.key} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ width: 210, textAlign: 'left', pr: 1, fontSize: 15 }}>{opt.label}</Typography>
                  <Box
                    onClick={handleSwatchClick(opt.key)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background:
                        customization[opt.key] !== undefined && customization[opt.key] !== ''
                          ? customization[opt.key]
                          : opt.default,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      mx: 1,
                      flexShrink: 0,
                    }}
                    aria-label={`Pick ${opt.label}`}
                  />
                  <TextField
                    label="Hex"
                    value={
                      customization[opt.key] !== undefined && customization[opt.key] !== ''
                        ? customization[opt.key]
                        : opt.default
                    }
                    onChange={e => setCustomization(prev => ({ ...prev, [opt.key]: e.target.value }))}
                    sx={{ width: 100, flexShrink: 0 }}
                    inputProps={{ maxLength: 7 }}
                  />
                </Box>
              ))}
              <Popover
                open={!!editingColorKey && BASIC_APPEARANCE_COLOR_OPTIONS.some(opt => opt.key === editingColorKey)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2 }}>
                  <HexColorPicker
                    color={customization[editingColorKey] || BASIC_APPEARANCE_COLOR_OPTIONS.find(opt => opt.key === editingColorKey)?.default || '#fff'}
                    onChange={handleColorChange}
                  />
                </Box>
              </Popover>
            </Box>
            <Typography variant="body2" gutterBottom>Chat Window Size</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Width (px)"
                type="number"
                value={customization.chatWidth === undefined ? '' : customization.chatWidth}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomization(prev => ({ ...prev, chatWidth: value === '' ? '' : value }));
                }}
                onBlur={(e) => {
                  let value = parseInt(e.target.value, 10);
                  if (isNaN(value)) value = 300;
                  if (value < 300) value = 300;
                  if (value > 800) value = 800;
                  setCustomization(prev => ({ ...prev, chatWidth: value }));
                }}
                inputProps={{ min: 300, max: 800 }}
                fullWidth
              />
              <TextField
                label="Height (px)"
                type="number"
                value={customization.chatHeight === undefined ? '' : customization.chatHeight}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomization(prev => ({ ...prev, chatHeight: value === '' ? '' : value }));
                }}
                onBlur={(e) => {
                  let value = parseInt(e.target.value, 10);
                  if (isNaN(value)) value = 400;
                  if (value < 400) value = 400;
                  if (value > 800) value = 800;
                  setCustomization(prev => ({ ...prev, chatHeight: value }));
                }}
                inputProps={{ min: 400, max: 800 }}
                fullWidth
              />
            </Box>
          </Box>
        );
      case 'logoSettings':
        const logoShape = customization.logoShape || 'round';
        const chatIconShape = customization.chatIconShape || 'round';
        const chatIconSurface = customization.chatIconSurface || 'solid';
        const chatIconShadow = customization.chatIconShadow !== false;
        const logoShapeRadius = logoShape === 'square' ? '0' : '50%';
        const chatIconShapeRadius = chatIconShape === 'square' ? '0' : '50%';
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ textAlign: 'left', mb: 0.5, color: 'text.secondary' }}>
              Upload logo to left side chat window.
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="logo-upload"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setCustomization(prev => ({ ...prev, logo: reader.result }));
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            <label htmlFor="logo-upload">
              <Tooltip title="Upload a transparent logo image (recommended size: 50x50px)" placement="top">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Upload Logo
                </Button>
              </Tooltip>
            </label>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Logo Shape</InputLabel>
              <Select
                value={logoShape}
                onChange={handleChange('logoShape')}
                label="Logo Shape"
              >
                <MenuItem value="round">Round</MenuItem>
                <MenuItem value="square">Square</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ textAlign: 'left', mb: 1, color: 'text.secondary', display: 'block' }}>
              {logoShape === 'round'
                ? 'Round: best with square PNG and transparent background (recommended 512x512 or larger).'
                : 'Square: best with square PNG/JPG and minimal outer padding (recommended 512x512 or larger).'}
            </Typography>
            {customization.logo && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  src={customization.logo}
                  alt="Bot logo"
                  style={{ maxWidth: '50px', maxHeight: '50px', width: '50px', height: '50px', objectFit: 'cover', borderRadius: logoShapeRadius }}
                />
              </Box>
            )}

            <Typography variant="body2" sx={{ textAlign: 'left', mb: 0.5, color: 'text.secondary' }}>
              Close chat window (upper right) to see chat icon.
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="chat-icon-upload"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setCustomization(prev => ({ ...prev, chatIcon: reader.result }));
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            <label htmlFor="chat-icon-upload">
              <Tooltip title="Upload a chat icon image (recommended size: 60x60px)" placement="top">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCamera />}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Upload Chat Icon
                </Button>
              </Tooltip>
            </label>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Chat Icon Shape</InputLabel>
              <Select
                value={chatIconShape}
                onChange={handleChange('chatIconShape')}
                label="Chat Icon Shape"
              >
                <MenuItem value="round">Round</MenuItem>
                <MenuItem value="square">Square</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ textAlign: 'left', mb: 1, color: 'text.secondary', display: 'block' }}>
              {chatIconShape === 'round'
                ? 'Round: use square PNG with transparent background for the cleanest circular crop.'
                : 'Square: use square PNG/JPG and keep the icon centered for best launcher fit.'}
            </Typography>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel>Chat Icon Surface</InputLabel>
              <Select
                value={chatIconSurface}
                onChange={handleChange('chatIconSurface')}
                label="Chat Icon Surface"
              >
                <MenuItem value="solid">Solid</MenuItem>
                <MenuItem value="floating">Floating</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ textAlign: 'left', mb: 1, color: 'text.secondary', display: 'block' }}>
              {chatIconSurface === 'floating'
                ? 'Floating: best for transparent PNG icons; removes launcher fill so the icon appears to float.'
                : 'Solid: adds a colored launcher background and works best for non-transparent images like JPG.'}
            </Typography>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Show Launcher Shadow</Typography>
                <Switch
                  checked={chatIconShadow}
                  onChange={handleSwitch('chatIconShadow')}
                  color="primary"
                />
              </Box>
            </FormControl>
            <Typography variant="caption" sx={{ textAlign: 'left', mb: 1, color: 'text.secondary', display: 'block' }}>
              {chatIconShadow
                ? 'Adds elevation to the launcher icon. Turn off for a flatter look.'
                : 'Shadow is disabled for a clean, flat launcher look.'}
            </Typography>
            <Box sx={{ mb: 1, color: 'text.secondary', textAlign: 'left' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Launcher style preview:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                <Typography component="li" variant="caption" sx={{ display: 'list-item' }}>Solid + Shadow On = raised button</Typography>
                <Typography component="li" variant="caption" sx={{ display: 'list-item' }}>Solid + Shadow Off = flat button</Typography>
                <Typography component="li" variant="caption" sx={{ display: 'list-item' }}>Floating + Shadow On = floating icon</Typography>
                <Typography component="li" variant="caption" sx={{ display: 'list-item' }}>Floating + Shadow Off = transparent flat icon</Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ textAlign: 'left', mb: 1, color: 'text.secondary', display: 'block' }}>
              Note: Transparent backgrounds require PNG or WebP with alpha. JPG does not support transparency.
            </Typography>
            {customization.chatIcon && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  src={customization.chatIcon}
                  alt="Chat icon"
                  style={{ maxWidth: '60px', maxHeight: '60px', width: '60px', height: '60px', objectFit: 'cover', borderRadius: chatIconShapeRadius }}
                />
              </Box>
            )}
          </Box>
        );
      case 'chat':
        return (
          <Box sx={{ p: 2 }}>
            {/* On Page Load Chat State Dropdown */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="onPageLoadChatState-label">On Page Load Chat State</InputLabel>
              <Select
                labelId="onPageLoadChatState-label"
                value={customization.onPageLoadChatState || 'open'}
                label="On Page Load Chat State"
                onChange={e => setCustomization(prev => ({ ...prev, onPageLoadChatState: e.target.value }))}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Enable Voice</Typography>
                <Switch
                  checked={customization.enableVoice === true}
                  onChange={handleSwitch('enableVoice')}
                  color="primary"
                />
              </Box>
            </FormControl>
            {/* Moved Placeholder Text here */}
            <TextField
              fullWidth
              label="Placeholder Text"
              value={customization.inputPlaceholder}
              onChange={handleChange('inputPlaceholder')}
              sx={{ mb: 2 }}
            />
            <TextField fullWidth label="Waiting Message" value={customization.waitingMessage} onChange={handleChange('waitingMessage')} sx={{ mb: 2 }} />

            <Divider sx={{ my: 2 }} />

            {/* Always show idle detection settings since it's now mandatory */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Idle Timeout</InputLabel>
              <Select
                value={customization.idleTimeout || 60}
                onChange={handleChange('idleTimeout')}
                label="Idle Timeout"
              >
                <MenuItem value={30}>30 seconds</MenuItem>
                <MenuItem value={60}>1 minute</MenuItem>
                <MenuItem value={120}>2 minutes</MenuItem>
                <MenuItem value={300}>5 minutes</MenuItem>
                <MenuItem value={600}>10 minutes</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Idle Message"
              multiline
              rows={2}
              value={customization.idleMessage}
              onChange={handleChange('idleMessage')}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Close Chat After</InputLabel>
              <Select
                value={customization.closeIdleTimeout || 300}
                onChange={handleChange('closeIdleTimeout')}
                label="Close Chat After"
              >
                <MenuItem value={300}>5 minutes</MenuItem>
                <MenuItem value={600}>10 minutes</MenuItem>
                <MenuItem value={1200}>20 minutes</MenuItem>
                <MenuItem value={1800}>30 minutes</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Close Message"
              multiline
              rows={2}
              value={customization.closeIdleMessage}
              onChange={handleChange('closeIdleMessage')}
            />
            {/* Test/Preview Buttons */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                onClick={onTestIdle}
                disabled={!onTestIdle}
                sx={{ mb: 1 }}
              >
                Test Idle Timeout
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={onTestClose}
                disabled={!onTestClose}
                sx={{ mb: 1 }}
              >
                Test Closing Chat
              </Button>
              <Button
                variant="outlined"
                color="info"
                fullWidth
                onClick={onResetPreview}
                disabled={!onResetPreview}
              >
                Reset Preview
              </Button>
            </Box>
          </Box>
        );
      case 'welcomeStep': {
        const intermediate = customization.intermediate && typeof customization.intermediate === 'object'
          ? customization.intermediate
          : {};
        const welcomeHasContent = Boolean(
          (typeof intermediate.mediaUrl === 'string' && intermediate.mediaUrl.trim())
          || (typeof intermediate.html === 'string' && intermediate.html.replace(/<[^>]*>/g, ' ').trim())
        );
        const welcomeEnabled = intermediate.forceOff === true
          ? false
          : (intermediate.enabled === true || welcomeHasContent);
        return (
          <Box sx={{ p: 2 }}>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Enable Welcome Step</Typography>
                <Switch
                  checked={welcomeEnabled}
                  onChange={(e) => updateIntermediate({ enabled: e.target.checked, forceOff: !e.target.checked })}
                  color="primary"
                />
              </Box>
            </FormControl>
            <Typography variant="caption" sx={{ textAlign: 'left', mb: 2, color: 'text.secondary', display: 'block' }}>
              Shown on page load only when On Page Load Chat State is Open. Agents set to Closed stay on the minimized launcher.
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Width"
              value={intermediate.width ?? customization.chatWidth ?? 400}
              onChange={(e) => updateIntermediate({ width: e.target.value === '' ? '' : Number(e.target.value) })}
              inputProps={{ min: 280, max: 800 }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Height"
              value={intermediate.height ?? customization.chatHeight ?? 600}
              onChange={(e) => updateIntermediate({ height: e.target.value === '' ? '' : Number(e.target.value) })}
              inputProps={{ min: 320, max: 900 }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Video or GIF URL"
              value={intermediate.mediaUrl || ''}
              onChange={(e) => updateIntermediate({ mediaUrl: e.target.value })}
              placeholder="https://example.com/welcome.mp4"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Title and description (HTML)"
              value={intermediate.html || ''}
              onChange={(e) => updateIntermediate({ html: e.target.value })}
              placeholder={'<h2>Title</h2>\n<p>Short description</p>'}
              helperText="Paste HTML. It is rendered in the welcome panel."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Button title"
              value={intermediate.buttonTitle ?? 'Start Conversation'}
              onChange={(e) => updateIntermediate({ buttonTitle: e.target.value })}
            />
          </Box>
        );
      }
      case 'messageColors':
        return (
          <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {MESSAGE_COLOR_OPTIONS.map(opt => (
              <Box
                key={opt.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  pl: 0.5,
                  pr: 0.5,
                }}
              >
                <Typography
                  sx={{
                    width: 185,
                    flexShrink: 0,
                    textAlign: 'left',
                    pr: 1,
                    fontSize: 15
                  }}
                >
                  {opt.label}
                </Typography>
                <Box
                  onClick={handleSwatchClick(opt.key)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background:
                      customization[opt.key] !== undefined && customization[opt.key] !== ''
                        ? customization[opt.key]
                        : (opt.key === 'botTextColor' || opt.key === 'userTextColor')
                          ? '#000000'
                          : '#fff',
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    mx: 1,
                    flexShrink: 0,
                  }}
                  aria-label={`Pick ${opt.label}`}
                />
                <TextField
                  label="Hex"
                  value={
                    customization[opt.key] !== undefined && customization[opt.key] !== ''
                      ? customization[opt.key]
                      : (opt.key === 'botTextColor' || opt.key === 'userTextColor')
                        ? '#000000'
                        : ''
                  }
                  onChange={e => setCustomization(prev => ({ ...prev, [opt.key]: e.target.value }))}
                  sx={{
                    width: 100,
                    flexShrink: 0,
                    '& .MuiInputBase-root': { height: 36 },
                    '& .MuiInputBase-input': { py: 0.5, fontSize: 13 }
                  }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            ))}
            <Popover
              open={!!editingColorKey}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2 }}>
                <HexColorPicker
                  color={customization[editingColorKey] || '#fff'}
                  onChange={handleColorChange}
                />
              </Box>
            </Popover>
            <Box sx={{ width: '100%', px: 0.5, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, pr: 0.5 }}>
                <Typography
                  sx={{
                    width: 185,
                    flexShrink: 0,
                    textAlign: 'left',
                    pr: 1,
                    fontSize: 15
                  }}
                >
                  {CHAT_CONTROLS_COLOR_OPTION.label}
                </Typography>
                <Box
                  onClick={handleSwatchClick(CHAT_CONTROLS_COLOR_OPTION.key)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background:
                      customization[CHAT_CONTROLS_COLOR_OPTION.key] !== undefined && customization[CHAT_CONTROLS_COLOR_OPTION.key] !== ''
                        ? customization[CHAT_CONTROLS_COLOR_OPTION.key]
                        : CHAT_CONTROLS_COLOR_OPTION.default,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    mx: 1,
                    flexShrink: 0,
                  }}
                  aria-label={`Pick ${CHAT_CONTROLS_COLOR_OPTION.label}`}
                />
                <TextField
                  label="Hex"
                  value={
                    customization[CHAT_CONTROLS_COLOR_OPTION.key] !== undefined && customization[CHAT_CONTROLS_COLOR_OPTION.key] !== ''
                      ? customization[CHAT_CONTROLS_COLOR_OPTION.key]
                      : CHAT_CONTROLS_COLOR_OPTION.default
                  }
                  onChange={e => setCustomization(prev => ({ ...prev, [CHAT_CONTROLS_COLOR_OPTION.key]: e.target.value }))}
                  sx={{
                    width: 100,
                    flexShrink: 0,
                    '& .MuiInputBase-root': { height: 36 },
                    '& .MuiInputBase-input': { py: 0.5, fontSize: 13 }
                  }}
                  inputProps={{ maxLength: 7 }}
                />
              </Box>
            </Box>
            <Button
              variant="outlined"
              onClick={handleResetMessageColors}
              color="info"
              fullWidth
            >
              RESET TO DEFAULT
            </Button>
          </Box>
        );
      case 'presets':
        // Defaults for starter prompt button colors
        const DEFAULT_STARTER_PROMPT_COLORS = {
          starterPromptTextColor: '#1976d2',
          starterPromptBorderColor: '#1976d2',
          starterPromptBackgroundColor: '#FFFFFF',
        };

        // Helper for color swatch and input
        const starterPromptColorOptions = [
          { key: 'starterPromptTextColor', label: 'Starter Prompt Text Color', default: '#1976d2' },
          { key: 'starterPromptBorderColor', label: 'Starter Prompt Border Color', default: '#1976d2' },
          { key: 'starterPromptBackgroundColor', label: 'Starter Prompt Background Color', default: '#FFFFFF' },
        ];

        const handleResetStarterPromptColors = () => {
          setCustomization(prev => ({ ...prev, ...DEFAULT_STARTER_PROMPT_COLORS }));
          setHasUnsavedChanges && setHasUnsavedChanges(true);
        };

        return (
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" gutterBottom>
              Add starter prompts that users can click instead of typing.
            </Typography>

            {customization.presetResponses?.map((preset, index) => (
              <Box
                key={preset.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  width: '100%',
                  minWidth: 0 // Important for flexbox text truncation
                }}
              >
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => {
                      const newResponses = [...customization.presetResponses];
                      if (index > 0) {
                        [newResponses[index], newResponses[index - 1]] = [newResponses[index - 1], newResponses[index]];
                        setCustomization(prev => ({ ...prev, presetResponses: newResponses }));
                      }
                    }}
                    disabled={index === 0}
                  >
                    <span style={{ fontSize: '18px' }}>↑</span>
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => {
                      const newResponses = [...customization.presetResponses];
                      if (index < newResponses.length - 1) {
                        [newResponses[index], newResponses[index + 1]] = [newResponses[index + 1], newResponses[index]];
                        setCustomization(prev => ({ ...prev, presetResponses: newResponses }));
                      }
                    }}
                    disabled={index === customization.presetResponses.length - 1}
                  >
                    <span style={{ fontSize: '18px' }}>↓</span>
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0, // Essential for text truncation in flex items
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                  onClick={() => handleEditPreset(preset)}
                >
                  {preset.text.length > 20 ? `${preset.text.substring(0, 20)}...` : preset.text}
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newResponses = customization.presetResponses.map(p =>
                        p.id === preset.id ? { ...p, active: !p.active } : p
                      );
                      setCustomization(prev => ({ ...prev, presetResponses: newResponses }));
                    }}
                    sx={{ color: preset.active ? 'primary.main' : 'text.disabled' }}
                  >
                    {preset.active ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newResponses = customization.presetResponses.filter(p => p.id !== preset.id);
                      setCustomization(prev => ({ ...prev, presetResponses: newResponses }));
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPreset}
              fullWidth
              sx={{ mt: 1 }}
            >
              Add Starter Prompts
            </Button>
            {/* Starter Prompt Button Color Pickers (moved below list) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 2 }}>
              {starterPromptColorOptions.map(opt => (
                <Box key={opt.key} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 210, textAlign: 'left', pr: 1, fontSize: 15 }}>{opt.label}</Typography>
                  <Box
                    onClick={e => {
                      setEditingColorKey(opt.key);
                      setAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background:
                        customization[opt.key] !== undefined && customization[opt.key] !== ''
                          ? customization[opt.key]
                          : opt.default,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      mx: 1,
                      flexShrink: 0,
                    }}
                    aria-label={`Pick ${opt.label}`}
                  />
                  <TextField
                    label="Hex"
                    value={
                      customization[opt.key] !== undefined && customization[opt.key] !== ''
                        ? customization[opt.key]
                        : opt.default
                    }
                    onChange={e => setCustomization(prev => ({ ...prev, [opt.key]: e.target.value }))}
                    sx={{ width: 100, flexShrink: 0 }}
                    inputProps={{ maxLength: 7 }}
                  />
                </Box>
              ))}
              <Popover
                open={!!editingColorKey && starterPromptColorOptions.some(opt => opt.key === editingColorKey)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2 }}>
                  <HexColorPicker
                    color={customization[editingColorKey] || starterPromptColorOptions.find(opt => opt.key === editingColorKey)?.default || '#fff'}
                    onChange={handleColorChange}
                  />
                </Box>
              </Popover>
              <Button
                variant="outlined"
                onClick={handleResetStarterPromptColors}
                color="info"
                fullWidth
                sx={{ mt: 1 }}
              >
                RESET COLORS TO DEFAULT
              </Button>
            </Box>
          </Box>
        );
      case 'quickReplies':
        // Defaults for quick reply colors
        const DEFAULT_QUICK_REPLY_COLORS = {
          quickReplyTextColor: '#1976d2',
          quickReplyBorderColor: '#1976d2',
          quickReplyBackgroundColor: '#FFFFFF',
        };

        // Helper for color swatch and input
        const quickReplyColorOptions = [
          { key: 'quickReplyTextColor', label: 'Quick Reply Text Color', default: '#1976d2' },
          { key: 'quickReplyBorderColor', label: 'Quick Reply Border Color', default: '#1976d2' },
          { key: 'quickReplyBackgroundColor', label: 'Quick Reply Background Color', default: '#FFFFFF' },
        ];

        const handleResetQuickReplyColors = () => {
          setCustomization(prev => ({ ...prev, ...DEFAULT_QUICK_REPLY_COLORS }));
          setHasUnsavedChanges && setHasUnsavedChanges(true);
        };

        return (
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" gutterBottom>
              Customize the appearance and behavior of quick reply buttons that appear below chat responses.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
              Enable Quick Replies
            </Typography>
            <Typography variant="body2" align='left' sx={{ mb: 1 }}>
              Turn quick reply buttons on or off. When enabled, suggested replies will appear below chat responses.
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Enable</Typography>
                <Switch
                  checked={customization.quickRepliesEnabled !== false}
                  onChange={handleSwitch('quickRepliesEnabled')}
                  color="primary"
                />
              </Box>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 2, mb: 2 }}>
              {quickReplyColorOptions.map(opt => (
                <Box key={opt.key} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 210, textAlign: 'left', pr: 1, fontSize: 15 }}>{opt.label}</Typography>
                  <Box
                    onClick={e => {
                      setEditingColorKey(opt.key);
                      setAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background:
                        customization[opt.key] !== undefined && customization[opt.key] !== ''
                          ? customization[opt.key]
                          : opt.default,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      mx: 1,
                      flexShrink: 0,
                    }}
                    aria-label={`Pick ${opt.label}`}
                  />
                  <TextField
                    label="Hex"
                    value={
                      customization[opt.key] !== undefined && customization[opt.key] !== ''
                        ? customization[opt.key]
                        : opt.default
                    }
                    onChange={e => setCustomization(prev => ({ ...prev, [opt.key]: e.target.value }))}
                    sx={{ width: 100, flexShrink: 0 }}
                    inputProps={{ maxLength: 7 }}
                  />
                </Box>
              ))}
              <Popover
                open={!!editingColorKey && quickReplyColorOptions.some(opt => opt.key === editingColorKey)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2 }}>
                  <HexColorPicker
                    color={customization[editingColorKey] || quickReplyColorOptions.find(opt => opt.key === editingColorKey)?.default || '#fff'}
                    onChange={handleColorChange}
                  />
                </Box>
              </Popover>
              <Button
                variant="outlined"
                onClick={handleResetQuickReplyColors}
                color="info"
                fullWidth
                sx={{ mt: 1 }}
              >
                RESET COLORS TO DEFAULT
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  // Expose saveAgentCustomization via ref for ChatMain
  useImperativeHandle(ref, () => ({
    saveAgentCustomization: () => saveAgentCustomization(customization),
    resetCustomization: handleResetAll,
  }), [customization, resetCustomization]);

  // --- Main render ---
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', width: MENU_WIDTH + DRAWER_WIDTH, minWidth: MENU_WIDTH + DRAWER_WIDTH, alignItems: 'flex-start', justifyContent: 'flex-start', overflow: 'visible' }}>
        {/* Left menu and controls */}
        <Box sx={{ width: MENU_WIDTH, minWidth: MENU_WIDTH, maxWidth: MENU_WIDTH, p: 1.25, pt: 1, pb: 1, borderRight: openDialog ? 1 : 0, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', height: 'auto', position: 'relative' }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', flexGrow: 1, pb: 8 }}>
            {/* Preview Device section removed for compactness */}
            <ToggleButtonGroup
              value={devicePreview}
              exclusive
              onChange={(e, val) => val && setDevicePreview(val)}
              fullWidth
              size="small"
              sx={{ mt: 0, mb: 1.25 }}
            >
              <Tooltip title="Mobile View (320px)"><ToggleButton value="mobile"><SmartphoneIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Tablet View (768px)"><ToggleButton value="tablet"><TabletIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Desktop View"><ToggleButton value="desktop"><LaptopIcon fontSize="small" /></ToggleButton></Tooltip>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%' }}>
              {MENU_OPTIONS.map(opt => (
                <Button
                  key={opt.key}
                  variant="text"
                  color={openDialog === opt.key ? 'primary' : 'inherit'}
                  onClick={(e) => {
                    e.preventDefault();
                    if (opt.key === 'agentInformation') {
                      setAgentInfoModalOpen(true);
                    } else if (opt.key === 'agentInstructions') {
                      setInstructionsModalOpen(true);
                    } else if (opt.key === 'agentActions') {
                      setTextBubbleModalOpen(true);
                    } else if (opt.key === 'greeting') {
                      setGreetingFallbackModalOpen(true);
                    } else {
                      setOpenDialog(opt.key);
                    }
                  }}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: openDialog === opt.key ? 600 : 400,
                    textDecoration: openDialog === opt.key ? 'underline' : 'none',
                    borderRadius: 0,
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: openDialog === opt.key ? 'color-mix(in srgb, var(--primary-color, #00AAFF) 8%, transparent)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'color-mix(in srgb, var(--primary-color, #00AAFF) 12%, transparent)'
                    }
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>
            {/* Save/Reset buttons moved up, right under menu */}
            {
              !onCustomizationSaved &&
              <>
                <Divider sx={{ my: 1.25 }} />
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, mb: 0.5 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    onClick={() => {
                      if (resetCustomization) {
                        handleResetAll();
                      } else if (window.confirm('Reset all customizations to default?')) {
                        setCustomization({
                          botTitle: 'Chat Bot',
                          botMessageColor: '#E3F2FD',
                          userMessageColor: '#F5F5F5',
                          chatBackgroundColor: '#F8F9FA',
                          headerBackgroundColor: '#FFFFFF',
                          footerBackgroundColor: '#FFFFFF',
                          inputPlaceholder: 'Type your message...',
                          inputBoxColor: '#FFFFFF',
                          sendIconColor: '#1976d2',
                          sendIconDisabledColor: 'rgba(0, 0, 0, 0.26)',
                          chatControlIconColor: '#555555',
                          showTimestamps: true,
                          timestampFormat: 'MM/DD/YYYY hh:mm a',
                          timestampColor: '#888888',
                          agentNameColor: '#000000',
                          botTitleColor: '#000000',
                          logoShape: 'round',
                          chatIconShape: 'round',
                          chatIconSurface: 'solid',
                          chatIconShadow: true,
                          waitingMessage: 'Thinking...',
                          chatHeight: 600,
                          chatWidth: 400,
                          fontSize: 'medium',
                          idleDetectionEnabled: true,
                          idleTimeout: 60,
                          idleMessage: "It looks like you're idle. Can I help you with anything else?",
                          closeIdleTimeout: 300,
                          closeIdleMessage: "Thank you for chatting with us, please reconnect at anytime.",
                          presetResponses: [],
                          soundEnabled: true,
                          isMuted: false,
                          visitorTranscriptEnabled: true,
                          onPageLoadChatState: 'open', // Default value for new agents
                          intermediate: {
                            enabled: false,
                            width: 400,
                            height: 600,
                            mediaUrl: '',
                            html: '',
                            buttonTitle: 'Start Conversation',
                          },
                        });
                      }
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      saveAgentCustomization(customization);
                      localStorage.setItem('chatCustomization', JSON.stringify(customization));
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </>
            }
          </Box>
          {/* Absolutely positioned Save Your Agent button for anchored placement */}
          {onCustomizationSaved && (
            <Box sx={{ position: 'absolute', left: 0, bottom: 24, width: '100%', px: 2 }}>
              <Divider sx={{ my: 1 }} />
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                color="primary"
                onClick={() => {
                  saveAgentCustomization(customization);
                  localStorage.setItem('chatCustomization', JSON.stringify(customization));
                }}
                sx={{ width: '100%', whiteSpace: 'nowrap', px: 1 }}
              >
                Save Your Agent
              </Button>
            </Box>
          )}
        </Box>
        {/* Agent Information Modal */}
        <AgentInformationModal
          open={agentInfoModalOpen}
          onClose={() => setAgentInfoModalOpen(false)}
          agentId={agentId}
        />
        {/* Agent Instructions Modal */}
        <AgentInstructionsModal
          open={instructionsModalOpen}
          onClose={() => setInstructionsModalOpen(false)}
          agentId={agentId}
          selectedInstructionId={selectedInstructionId}
          setSelectedInstructionId={setSelectedInstructionId}
          onInstructionChange={() => { }}
        />
        {/* Text Bubble Modal */}
        <TextBubbleModal
          open={textBubbleModalOpen}
          onClose={() => setTextBubbleModalOpen(false)}
          customization={customization}
          setCustomization={setCustomization}
          setHasUnsavedChanges={setHasUnsavedChanges}
          agentId={agentId}
          onSave={saveAgentCustomization}
        />
        <GreetingFallbackModal
          open={greetingFallbackModalOpen}
          onClose={() => setGreetingFallbackModalOpen(false)}
          customization={customization}
          setCustomization={setCustomization}
          setHasUnsavedChanges={setHasUnsavedChanges}
          onSave={saveAgentCustomization}
        />
        <AdvancedEmailSettingsModal
          open={advancedEmailModalOpen}
          onClose={closeAdvancedEmailSettings}
          agentId={agentId}
          customization={customization}
          setCustomization={setCustomization}
          setAlert={setAlert}
          onSaved={handleAdvancedEmailSaved}
        />
        {/* Right-side drawer as a Box, not a Drawer */}
        {openDialog && (
          <Box
            sx={{
              width: DRAWER_WIDTH,
              boxShadow: 3,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              position: 'relative',
              borderLeft: 1,
              borderColor: 'divider',
              minWidth: DRAWER_WIDTH,
              maxWidth: DRAWER_WIDTH,
              minHeight: 200,
              maxHeight: '90vh',
              height: 'auto',
              alignSelf: 'flex-start',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            {/* Close button at top right */}
            <IconButton onClick={() => setOpenDialog(null)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 10 }} aria-label="close">
              <CloseIcon />
            </IconButton>
            {/* Title below close button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', p: 2, borderBottom: 1, borderColor: 'divider', pl: 2, pr: 6 }}>
              <Typography variant="h6">{MENU_OPTIONS.find(opt => opt.key === openDialog)?.label}</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, pb: 4 }}>
              {renderDialogContent(openDialog)}
            </Box>
          </Box>
        )}
        {/* Preset Response Edit Dialog (unchanged) */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Preset Response</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Response Text"
              fullWidth
              multiline
              rows={4}
              value={editingPresetText}
              onChange={(e) => setEditingPresetText(e.target.value)}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePreset} color="primary">Save</Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Removed duplicate Save Your Agent button at the bottom. Only the anchored button remains. */}
    </>
  );
});

export default BotCustomizerPanel;
